import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { EndorsementsPageErrorHandlerProps } from './EndorsementsPageErrorHandler';
import type { EndorsementsPagePrefetchedContext } from './hook';
import { useEndorsementsPageContextState } from './hook';
import { EndorsementsLayoutPageDataContextProvider } from './pageData';

type Props = PropsWithChildren<{
	slug: string;
	prefetchedData: EndorsementsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<EndorsementsPageErrorHandlerProps>;
	loadingFallback: JSX.Element;
}>;

export const EndorsementsPageContextProvider: React.FC<Props> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = useEndorsementsPageContextState(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<EndorsementsLayoutPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ petition_id: `petition_${pageData.data.petition.id}` }}>
				{children}
			</TrackingContext>
		</EndorsementsLayoutPageDataContextProvider>
	);
};
